<template>
    <div class="quick-view-panel" :class="{'quick-view-panel_isBtnWatchEnable': featureBtnWatchEnabled}">
        <div class="quick-view-panel__wrap-img" :class="{'quick-view-panel__wrap-img-channel': !isProgramAvailable}">
            <img
                v-show="quickViewPanel.srcImg"
                class="quick-view-panel__img"
                :class="{'quick-view-panel__img_blured': isParentallyRestricted, 'quick-view-panel__img-channel': !isProgramAvailable}" 
                :src="quickViewPanel.srcImg" 
                :alt="quickViewPanel.programName"
                loading="lazy"
            >
            <common-icon v-show="!quickViewPanel.srcImg" class="quick-view-panel__img" :icon-name="'IconNoPicture'" />
            <common-icon v-show="isParentallyRestricted" class="quick-view-panel__img-lock" :icon-name="'IconLock'" />
        </div>
        <div class="quick-view-panel__information">
            <div class="quick-view-panel__programm-name" :class="{'quick-view-panel__programm-name_skeleton': isSkeletonVisible}">
                {{ quickViewPanel.programName }}
            </div>
            <div class="quick-view-panel__dashboard" :class="{'quick-view-panel__dashboard_skeleton': isSkeletonVisible}">
                <div class="quick-view-panel__channel-number">{{ quickViewPanel.channelNumber }}</div>
                <div class="quick-view-panel__channel-name">{{ quickViewPanel.channelName }}</div>
                <program-labels
                    :advisory-text="quickViewPanel.advisoryText"
                    :high-definition="quickViewPanel.highDefinition"
                    :closed-captions="quickViewPanel.closedCaptions"
                    :descriptive-audio="quickViewPanel.descriptiveAudio"
                    :is-restart-available="quickViewPanel.isRestartAvailable"
                />
            </div>
            <div class="quick-view-panel__programm-description" :class="{'quick-view-panel__programm-description_skeleton': isSkeletonVisible}">
                <common-icon v-if="isParentallyRestricted && focusedProgram && isChannelsAvailable" class="quick-view-panel__programm-description-img-lock" :icon-name="'IconLock'" />
                <div v-else>{{ quickViewPanel.programDescription }}</div>
            </div>
        </div>
        <div class="quick-view-panel__dashboard-buttons">
            <div class="quick-view-panel__dashboard-buttons-inner">
                <button 
                    v-if="isBtnWatchEnable"
                    id="liveBtn" 
                    ref="liveBtn" 
                    :data-next="'liveBtn'" 
                    class="quick-view-panel__button" 
                    @click="goLive"
                >
                    {{ guideBtnWatch }}
                </button>
                <button
                    v-if="isBtnRestartEnable" 
                    id="restartBtn" 
                    ref="restartBtn" 
                    :data-prev="'liveBtn'" 
                    :data-next="'page-content'" 
                    class="quick-view-panel__button" 
                    @click="goRestart"
                >
                    <common-icon :icon-name="'IconRestart'" />
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { type Ref, ref, computed, watch, type PropType } from "vue";
    import { type ProgramGuideItem, type Channel } from '@ca.vmedia.tv/domains';
    import { Strings } from "@/locales/types/enums/strings";
    import { useChannelsStore } from "@/stores/Channels";
    import { ProgramUtils } from "@/utils";
    import { useI18n } from "vue-i18n";
    import { useParentControl } from "@/composables/parentControl/useParentControl"
    import { storeToRefs } from 'pinia';
    import { useGuideQuickViewPanel } from "@/composables/GuideQuickViewPanel/useGuideQuickViewPanel";
    import { useGuideStore } from "@/stores/Guide";

    const GuideStoreModule = useGuideStore();
    const {
        channelsViewPortIsEmpty,
    } = storeToRefs(GuideStoreModule)

    const ChannelsStore = useChannelsStore();
    const {
        getAvailableChannels: allChannels,
    } = storeToRefs(ChannelsStore);

    const props = defineProps({
        focusedProgram: {
            type: Object as PropType<ProgramGuideItem | null>,
            default: function () {
                return null;
            } 
        }
    });

    const emit = defineEmits(['go-restart', 'go-live'])
    const { guideConfig } = useAppConfig();
    const { t } = useI18n();
    const { needsUnlockPin } = useParentControl()
    const featureGuideRestartBtnsEnabled = guideConfig.featureGuideRestartBtnsEnabled;
    const featureBtnWatchEnabled = guideConfig.featureBtnWatchEnabled;
    const guideBtnWatch: string = t(Strings.GUIDE_BTN_WATCH).toString();
    const isSkeletonVisible: Ref<boolean> = ref(true);

    const {
        quickViewPanel,
        setQuickViewPanel,
        isProgramAvailable,
        isChannelsAvailable,
    } = useGuideQuickViewPanel();

    const isBtnWatchEnable = computed((): boolean => Boolean(featureBtnWatchEnabled && props.focusedProgram && isLive.value));
    const isBtnRestartEnable = computed((): boolean => Boolean(featureGuideRestartBtnsEnabled && quickViewPanel.isRestartAvailable && props.focusedProgram && isLive.value));
    const isLive = computed((): boolean => props.focusedProgram === null ? false : ProgramUtils.isLive(props.focusedProgram));
    const channel = computed((): Channel | null => allChannels.value.find(item => item.id === props.focusedProgram?.recentAirTime?.channelId) ?? null);
    const isParentallyRestricted = computed((): boolean => needsUnlockPin(props.focusedProgram?.advisory ?? channel.value?.advisory));

    const goRestart = () => {
        emit("go-restart");
    };
    const goLive = () => {
        emit("go-live");
    };

    watch(
        ()=> props.focusedProgram,
        (program) => {
            if (program) {
                isSkeletonVisible.value = false;
            }
            setQuickViewPanel(program, channel.value);
        },
        { immediate: true }
    );
    watch(
        channelsViewPortIsEmpty,
        () => {
            isSkeletonVisible.value = false;
        }
    );
</script>

<style lang="scss">
    @import url("styles.scss");
</style>